import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import {
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";
interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_PRD_WMO_REG_001_POP_02 = forwardRef(
  ({ selectedData }: popupProps, ref: React.Ref<any>) => {
    //#region variable
    const { userInfo } = global();
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });
    const searchRef = useRef<any>(null);
    //#endregion

    //#region Components useState
    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [gridFocus, setGridFocus] = useState(false);
    //#endregion
    const [addInfoFlags, setAddInfoFlags] = useState<string[]>([]);

    useImperativeHandle(ref, () => ({
      getSelectedRow: () => [
        gridRefs.current["grid1"].current?.getSelectedRows() || [],
        gridRefs.current["grid2"].current?.getSelectedRows() || [],
      ],
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PRD_WMO_REG_001_POP_02",
            {}
          );
          const mergedData: any = { ...rtn.formData, ...selectedData };
          const { flags, flagNames, flagOrders } =
            await loadAddInfoFlags("grid2");

          const baseGrid1Col = rtn?.grid?.grid2?.[0] || {};
          const nextSeqBase = Array.isArray(rtn?.grid?.grid2)
            ? rtn.grid.grid1.length
            : 0;
          const addInfoColumns = flags.map((flag: string, index: number) => ({
            ...baseGrid1Col,
            cd_column: flag,
            nm_column: flagNames[flag] || flag,
            tp_column: "SnTextBox",
            fg_visible: "Y",
            yn_required: "N",
            yn_sum: "N",
            yn_allowsorting: "Y",
            yn_fixed: "N",
            qt_width: 120,
            no_seq: nextSeqBase + (flagOrders[flag] || index + 1),
            dc_editcon: "",
            cd_popup: "",
            tp_format: "",
            nm_group: "",
          }));
          setFormControlData(rtn.formControlData); // 조회조건 항목 설정
          setFormData(mergedData); // 조회조건 초기값
          setColums({
            //grid1: rtn.grid.grid1,
            grid1: rtn.grid.grid1,
            grid2: [...rtn.grid.grid2, ...addInfoColumns],
          });
        } catch (error) {}
      };
      fetchform();
    }, [userInfo]);
    useEffect(() => {
      gridRefs.current["grid1"].current.selectAndFocusRow(0);
      setGridFocus(false);
    }, [gridFocus]);

    //#region Button Event
    const onSearch = () => {
      const fetchdatasource = async () => {
        try {
          const params = {
            id: "sn_prd_wmo_reg_001_pop_01_02",
            params: searchRef.current.getFormdata(),
          };

          const data: any = await fnGetDataSet(params);
          const flags =
            addInfoFlags.length > 0
              ? addInfoFlags
              : (await loadAddInfoFlags("grid2")).flags;
          const sourceRows = (data[1] || []).map((row: any) => ({
            ...row,
            js_addinfo: row?.js_addinfo ?? row?.js_addinfo_h,
          }));
          if (userInfo.cd_firm === "8300") {
            setDataSources({
              grid1: data[0],
              grid2: data[2],
            });
          } else {
            setDataSources({
              grid1: data[0],
              grid2: hydrateRowsWithAddInfo(sourceRows, flags),
            });
          }

          if (data[0] && data[0].length > 0) {
            const selectNoEst = data[0][0];

            // 1. 그리드 데이터가 바인딩되었음을 기다림
            await new Promise((resolve) => setTimeout(resolve, 50)); // 비동기 반영 대기 (최적화 가능)

            // 2. 첫 번째 행 선택 및 포커스 설정
            setGridFocus(true);
          }

          gridRefs.current["grid1"].current.resetUpdate();
          gridRefs.current["grid2"].current.resetUpdate();
        } catch (error: any) {
          showDialog("error", error.message);
        }
      };
      fetchdatasource();
    };
    //#endregion

    //#region Event Method
    const onFocusedRowChanged = (event: any) => {
      const selectedRow = event; // 클릭한 행 데이터
      if (selectedRow) {
        // 배열로 만들어서 필터링 함수 호출
        const no_woreq = selectedRow.no_woreq;
        gridRefs.current["grid2"].current.applyFilters({ no_woreq: no_woreq });
        gridRefs.current["grid2"].current.selectAndFocusRow(0);
        // gridApiRefL.current.selectAllFiltered();
      } else {
        return;
      }
    };
    //#endregion
    const loadAddInfoFlags = async (idTarget = "grid2") => {
      try {
        const data: any = await fnGetDataSet({
          id: "sys_addinfo_pop_01_01",
          params: {
            cd_firm: userInfo?.cd_firm ?? "",
            cd_menu: "SN_SAL_SAO_REG_001",
            id_target: idTarget,
          },
        });

        const metaRows = data?.[0] || [];
        const orderedMetaRows = [...metaRows].sort(
          (a: any, b: any) => Number(a?.no_line ?? 0) - Number(b?.no_line ?? 0)
        );
        const flags = extractAddInfoFlags(orderedMetaRows);
        const flagNames = (orderedMetaRows || []).reduce(
          (acc: Record<string, string>, row: any) => {
            const cdFlag = String(row?.cd_flag ?? "").trim();
            const nmFlag = String(row?.nm_flag ?? "").trim();
            if (cdFlag) {
              acc[cdFlag] = nmFlag || cdFlag;
            }
            return acc;
          },
          {}
        );
        const flagOrders = (orderedMetaRows || []).reduce(
          (acc: Record<string, number>, row: any) => {
            const cdFlag = String(row?.cd_flag ?? "").trim();
            const noLine = Number(row?.no_line ?? 0);
            if (cdFlag) {
              acc[cdFlag] = Number.isFinite(noLine) ? noLine : 0;
            }
            return acc;
          },
          {}
        );

        setAddInfoFlags(flags);
        return { flags, flagNames, flagOrders };
      } catch (error: any) {
        setAddInfoFlags([]);
        return {
          flags: [],
          flagNames: {} as Record<string, string>,
          flagOrders: {} as Record<string, number>,
        };
      }
    };
    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onFocusedRowChanged: onFocusedRowChanged,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {},
        },
      }),
      [columns, dataSources]
    );
    return (
      <div>
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div style={{ textAlign: "center" }}>
          <SnSearchBtn
            text={"조회"}
            onClick={onSearch}
            sx={{
              width: 80,
              marginTop: 1.5,
            }}
            spinner={true}
          />
        </div>
        <div>
          <div style={{ height: "25vh", marginTop: "10px" }}>
            <SnGrid
              ref={gridRefs.current["grid1"]}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              etcProps={{ rowSelection: "single" }}
              events={grids.grid1.events}
            />
          </div>
          <div style={{ height: "25vh", marginTop: "10px" }}>
            <SnGrid
              ref={gridRefs.current["grid2"]}
              columnConfig={grids.grid2.columns}
              rowData={grids.grid2.dataSource}
              etcProps={{ rowSelection: "single" }}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default SN_PRD_WMO_REG_001_POP_02;
